import React, {ReactNode, useCallback, useState} from 'react';
import {ReactKeycloakProvider} from '@react-keycloak/web';
import {AuthClientError, AuthClientEvent} from '@react-keycloak/core/lib/types';
import {Spinner} from '@zvne/fe-framework/dist/presentation/loading/spinner';
import {ErrorIcon} from '@zvne/fe-framework/dist/presentation/loading/error-icon';
import {keycloak} from '../../../data-access/reusable/keycloak';
import {Auth, AuthContext} from './auth-context';

enum KeycloakState {
    LOADING,
    READY,
    ERROR
}

export interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider(props: AuthProviderProps) {
    const [keycloakState, setKeycloakState] = useState(KeycloakState.LOADING);
    const [auth, setAuth] = useState<Auth>({
        isAuthenticated: false,
        username: '',
        hasRole: hasRole
    });

    const handleKeycloakEvent = useCallback((eventType: AuthClientEvent, error?: AuthClientError) => {
        if (eventType === 'onReady') {
            setKeycloakState(KeycloakState.READY);
        } else if (eventType === 'onInitError') {
            console.error('Failed to initialise keycloak.');
            console.error(error);
            setKeycloakState(KeycloakState.ERROR);
        }
        const newAuth: Auth = {
            isAuthenticated: keycloak.authenticated === true,
            username: keycloak.authenticated === true ? (keycloak.tokenParsed as any).preferred_username : '',
            hasRole: keycloak.hasRealmRole
        };
        if (newAuth.isAuthenticated !== auth.isAuthenticated || newAuth.username !== auth.username) {
            setAuth(newAuth);
        }
    }, [auth]);

    return (
        <ReactKeycloakProvider authClient={keycloak} onEvent={handleKeycloakEvent}>
            <AuthContext.Provider value={auth}>
                {keycloakState === KeycloakState.LOADING &&
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                >
                    <Spinner size={'3x'}/>
                </div>
                }
                {keycloakState === KeycloakState.ERROR &&
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                >
                    <ErrorIcon size={'3x'}/>
                </div>
                }
                {keycloakState === KeycloakState.READY && props.children}
            </AuthContext.Provider>
        </ReactKeycloakProvider>
    );
}

function hasRole(role: string): boolean {
    return keycloak.authenticated === true && keycloak.hasRealmRole(role);
}
